// client/src/api/ApiUsers.js
import flex from './FlexFetch'
import ApiAuth from './ApiAuth'
import { isMobilePlatform, setMobileAuthToken } from '../utils/mobileAuth.js'
import { unwrapStrictData } from './contractUtils'

const user_endpoint = '/api/1.0/users/'

async function getAll() {
  const response = await flex.flexFetch(user_endpoint + 'all', {
    credentials: 'include',
  })
  const data = unwrapStrictData(await flex.handleResponse(response))
  return Array.isArray(data) ? data : (data?.items ?? [])
}

async function create(user) {
  const response = await flex.flexFetch(user_endpoint + 'create', {
    method: 'POST',
    body: JSON.stringify(user),
    credentials: 'include',
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function update(user) {
  const response = await flex.flexFetch(user_endpoint + 'update', {
    method: 'PUT',
    body: JSON.stringify(user),
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function remove(user_id) {
  const response = await flex.flexFetch(user_endpoint + 'delete', {
    method: 'PUT',
    body: JSON.stringify({ user_id: user_id }),
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function getUserById(user_id) {
  const response = await flex.flexFetch(
    user_endpoint + `get?user_id=${user_id}`,
  )
  return unwrapStrictData(await flex.handleResponse(response))
}

/**
 * Load the authenticated user's profile.
 * @deprecated Call ApiAuth.me() directly for current-user reads; this wrapper remains for legacy callers while ApiUsers continues to own profile mutations such as updatePhone().
 */
async function getMyUser(options = {}) {
  return ApiAuth.me(options)
}

/**
 * Check if a user exists by email
 * @param {string} email - Email address to check
 * @returns {Promise<boolean>} True if user exists, false otherwise
 */
async function checkExisting(email) {
  const response = await flex.flexFetch(user_endpoint + 'checkExisting', {
    method: 'POST',
    body: JSON.stringify({ email }), // Backend expects { email: string }
  })
  const data = unwrapStrictData(await flex.handleResponse(response))
  return data.exists
}

/**
 * Authenticate a user with email and password.
 * auth = { email: string, password: string, rememberMe?: boolean }
 * @deprecated Call ApiAuth.login() directly; this wrapper will be removed once callers are updated.
 */
async function verifyUser(auth) {
  return ApiAuth.login(auth)
}

async function logout() {
  return ApiAuth.logout()
}

async function changePassword({ current_password, new_password }) {
  const response = await flex.flexFetch('/api/1.0/users/changePassword', {
    method: 'POST',
    body: JSON.stringify({ current_password, new_password }),
    credentials: 'include',
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function adminSetPassword({ user_id, new_password }) {
  const response = await flex.flexFetch('/api/1.0/users/adminSetPassword', {
    method: 'POST',
    body: JSON.stringify({ user_id, new_password }),
    credentials: 'include',
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function forgotPassword({ email }) {
  const response = await flex.flexFetch('/api/1.0/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function resetPassword({ token, new_password }) {
  const response = await flex.flexFetch('/api/1.0/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, new_password }),
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function uploadAvatar(file) {
  const formData = new FormData()
  formData.append('avatar', file)

  const response = await flex.flexFetch('/api/1.0/users/upload-avatar', {
    method: 'POST',
    body: formData, // Don't stringify FormData
    credentials: 'include',
    // Don't set Content-Type header - let browser set it with boundary
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

// Social authentication functions
// Note: Google sign-in removed from client. Apple remains available.

async function appleAuth(identityToken, profile = null) {
  const response = await flex.flexFetch('/api/1.0/auth/apple-auth', {
    method: 'POST',
    body: JSON.stringify({ identityToken, profile }),
    credentials: 'include',
  })

  if (response.status === 200) {
    const data = unwrapStrictData(await flex.handleResponse(response))
    // For mobile platforms, extract and store the token
    if (isMobilePlatform() && data.token) {
      setMobileAuthToken(data.token)
    }
    return data
  }
  return false
}

async function getSocialAccounts() {
  const response = await flex.flexFetch('/api/1.0/auth/get-social-accounts', {
    method: 'GET',
    credentials: 'include',
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

async function unlinkSocialAccount(provider, accountId) {
  const response = await flex.flexFetch('/api/1.0/auth/unlink-social-account', {
    method: 'POST',
    body: JSON.stringify({ provider, accountId }),
    credentials: 'include',
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

/**
 * Update user's phone number and SMS consent status
 * @param {Object} data - { phone_e164?: string, sms_consent_status?: 'granted' | 'none' | 'revoked' }
 * @returns {Promise<Object>} Updated user object
 */
async function updatePhone(data) {
  const response = await flex.flexFetch('/api/1.0/users/update-phone', {
    method: 'POST',
    body: JSON.stringify(data),
    credentials: 'include',
  })
  return unwrapStrictData(await flex.handleResponse(response))
}

const ApiUsers = {
  getAll,
  create,
  remove,
  getMyUser,
  getUserById,
  checkExisting,
  update,
  verifyUser,
  logout,
  changePassword,
  adminSetPassword,
  forgotPassword,
  resetPassword,
  uploadAvatar,
  appleAuth,
  getSocialAccounts,
  unlinkSocialAccount,
  updatePhone,
}

export default ApiUsers
